import { useEffect, useState } from 'react'
import MaterialsService, { type SharedMaterial } from '../services/MaterialsService'
import SavedConfigurationsService from '../services/SavedConfigurationsService'
import ProfilesService from '../services/ProfilesService'
import { isValidEmail } from '../utils/validation'
import { translateFirebaseError } from '../utils/firebaseErrors'
import { auth } from '../config/firebase'
import { EmailAuthProvider, GoogleAuthProvider, reauthenticateWithCredential, reauthenticateWithPopup, updateEmail as fbUpdateEmail, updatePassword as fbUpdatePassword, deleteUser } from 'firebase/auth'
import type { SavedConfiguration } from '../types'

interface ClientPanelProps {
  isOpen: boolean
  onClose: () => void
  isLoggedIn?: boolean
  currentUser?: { id: string; username: string; email: string }
  onLogout?: () => void
  onProfileUpdated?: (user: { id: string; username: string; email: string }) => void
  onLoadConfiguration?: (productCode: string) => void
}

type ClientView = 'dashboard' | 'settings' | 'configs' | 'orders' | 'materials' | 'profile'

// Separate component: edits only the currently logged-in user's own data
const ClientProfileView = ({ onBack, currentUser, onProfileSaved, onAccountDeleted }: { 
  onBack: () => void; 
  currentUser?: { id: string; username: string; email: string }; 
  onProfileSaved?: (u: { id: string; username: string; email: string }) => void;
  onAccountDeleted?: () => void;
}) => {
  const [email, setEmail] = useState(currentUser?.email ?? '')
  const [companyName, setCompanyName] = useState('')
  const [originalCompanyName, setOriginalCompanyName] = useState('')
  const [password, setPassword] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [emailError, setEmailError] = useState<string | undefined>(undefined)
  const [checkingEmail, setCheckingEmail] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [deleting, setDeleting] = useState(false)

  // Check if current user is a Google user
  const isGoogleUser = auth.currentUser?.providerData.some(provider => provider.providerId === 'google.com') || false

  useEffect(() => {
    // keep local email in sync if currentUser changes
    setEmail(currentUser?.email ?? '')
    setEmailError(undefined)
  }, [currentUser?.email])

  // Profile data is loaded within ClientProfileView component when needed
  // Removed auto-loading to prevent excessive Firebase queries

  const save = async () => {
    if (!currentUser?.id) return
    if (email && !isValidEmail(email)) { alert('Podaj poprawny adres e‑mail.'); return }
    const emailChanged = (email ?? '').trim() !== (currentUser?.email ?? '').trim()
    setLoading(true)
    try {
      // If changing email or password, require current password verification (reauth)
      const user = auth.currentUser
      if (!user) throw new Error('Brak zalogowanego użytkownika')
      if (emailChanged || password) {
        if (!currentPassword) { alert('Podaj obecne hasło, aby zapisać zmiany.'); setLoading(false); return }
        const cred = EmailAuthProvider.credential(user.email || '', currentPassword)
        await reauthenticateWithCredential(user, cred)
      }

      // If email changed, ensure it's not taken (extra guard; service also enforces)
      if (emailChanged) {
        const taken = await ProfilesService.isEmailTaken(email.trim(), currentUser.id)
        if (taken) { setEmailError('Adres e‑mail jest już zajęty.'); setLoading(false); return }
      }

      // Change email in Firebase Auth (if changed)
      if (emailChanged) {
        // Extra guard for duplicate email via Firestore-based check (optional)
        const taken = await ProfilesService.isEmailTaken(email.trim(), currentUser.id)
        if (taken) { setEmailError('Adres e‑mail jest już zajęty.'); setLoading(false); return }
        await fbUpdateEmail(user, email.trim())
      }

      // Change password in Firebase Auth (if provided)
      if (password) {
        await fbUpdatePassword(user, password)
      }

      // Update profile with email and/or company name changes
      const updates: any = {}
      if (emailChanged) {
        updates.email = email
      }
      const companyChanged = (companyName ?? '').trim() !== (originalCompanyName ?? '').trim()
      if (companyChanged) {
        updates.companyName = companyName.trim()
      }
      
      if (Object.keys(updates).length > 0) {
        console.log('Updating profile with:', updates)
        try {
          await ProfilesService.updateProfile(currentUser.id, updates)
          // Update original value after successful save
          if (companyChanged) {
            setOriginalCompanyName(companyName.trim())
          }
          console.log('Profile updated successfully')
        } catch (error: any) {
          console.error('Error updating profile:', error)
          alert(`Błąd podczas zapisywania: ${error.message || error}`)
          setLoading(false)
          return
        }
      }
      
      alert('Zapisano zmiany')
      setPassword('')
      setCurrentPassword('')
      if (emailChanged) {
        onProfileSaved?.({ id: currentUser.id, username: currentUser.username, email })
      }
    } catch (e) {
      alert(translateFirebaseError(e))
    } finally {
      setLoading(false)
    }
  }

  const deleteAccount = async () => {
    if (!currentUser?.id) return
    
    setDeleting(true)
    try {
      const user = auth.currentUser
      if (!user) throw new Error('Brak zalogowanego użytkownika')
      
      // Sprawdź czy użytkownik logował się przez Google
      const isGoogleUser = user.providerData.some(provider => provider.providerId === 'google.com')
      
      if (isGoogleUser) {
        // Dla użytkowników Google - reautentykacja przez popup
        const provider = new GoogleAuthProvider()
        await reauthenticateWithPopup(user, provider)
      } else {
        // Dla użytkowników email/password - wymagaj hasło
        if (!deletePassword.trim()) {
          alert('Podaj hasło, aby usunąć konto.')
          setDeleting(false)
          return
        }
        const cred = EmailAuthProvider.credential(user.email || '', deletePassword)
        await reauthenticateWithCredential(user, cred)
      }
      
      // Usuń profil z Firestore
      await ProfilesService.deleteProfile(currentUser.id)
      
      // Usuń konto Firebase Auth
      await deleteUser(user)
      
      // Powiadom o usunięciu konta
      alert('Konto zostało usunięte.')
      onAccountDeleted?.()
    } catch (err: any) {
      alert(translateFirebaseError(err))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="admin-placeholder-view">
      <div className="admin-view-header">
        <button className="btn-back" onClick={onBack}>
          ← Powrót do panelu głównego
        </button>
        <h3>Moje dane</h3>
      </div>
      <div className="admin-form" style={{ maxWidth: 680 }}>
        {!currentUser ? (
          <div style={{ color: '#6b7280' }}>Musisz być zalogowany, aby edytować swoje dane.</div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="form-group">
              <label>Login</label>
              <input type="text" value={currentUser.username} disabled />
            </div>
            <div className="form-group">
              <label>E‑mail</label>
              <input
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setEmailError(undefined) }}
                onBlur={async () => {
                  const v = (email ?? '').trim()
                  const orig = (currentUser?.email ?? '').trim()
                  if (!v || v === orig) { setEmailError(undefined); return }
                  if (!isValidEmail(v)) { setEmailError('Nieprawidłowy format e‑mail.'); return }
                  setCheckingEmail(true)
                  try {
                    const taken = await ProfilesService.isEmailTaken(v, currentUser?.id)
                    setEmailError(taken ? 'Adres e‑mail jest już zajęty.' : undefined)
                  } finally { setCheckingEmail(false) }
                }}
                pattern="[^\s@]+@[^\s@]+\.[^\s@]{2,}"
              />
              {emailError && <div style={{ color: '#b91c1c', fontSize: 12 }}>{emailError}</div>}
            </div>
            <div className="form-group">
              <label>Nazwa firmy (opcjonalnie)</label>
              <input
                type="text"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="np. ABC Sp. z o.o."
              />
            </div>
            <div className="form-group">
              <label>Nowe hasło</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} />
            </div>
            {(password || ((email ?? '').trim() !== (currentUser?.email ?? '').trim())) && (
              <div className="form-group">
                <label>Obecne hasło (wymagane do zmiany)</label>
                <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" disabled={loading || !!emailError || checkingEmail || (email ? !isValidEmail(email) : false)} onClick={save}>{loading ? 'Zapisywanie…' : 'Zapisz'}</button>
            </div>
            <div style={{ color: '#6b7280', fontSize: 12 }}>Zmiany dotyczą wyłącznie Twojego konta.</div>

            {/* Sekcja usunięcia konta */}
            <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #e5e5e5' }}>
              <h4 style={{ color: '#dc2626', marginBottom: '10px' }}>Strefa niebezpieczna</h4>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '15px' }}>
                Usunięcie konta jest nieodwracalne. Wszystkie Twoje dane zostaną permanentnie usunięte.
              </p>
              
              {!showDeleteConfirm ? (
                <button 
                  className="btn" 
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{ 
                    backgroundColor: '#dc2626', 
                    color: 'white', 
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Usuń konto
                </button>
              ) : (
                <div style={{ border: '1px solid #dc2626', borderRadius: '4px', padding: '15px', backgroundColor: '#fef2f2' }}>
                  <p style={{ fontSize: '14px', fontWeight: '500', color: '#dc2626', marginBottom: '10px' }}>
                    Czy na pewno chcesz usunąć swoje konto?
                  </p>
                  {!isGoogleUser && (
                    <div className="form-group" style={{ marginBottom: '15px' }}>
                      <label>Podaj hasło, aby potwierdzić:</label>
                      <input 
                        type="password" 
                        value={deletePassword} 
                        onChange={e => setDeletePassword(e.target.value)}
                        placeholder="Twoje obecne hasło"
                      />
                    </div>
                  )}
                  {isGoogleUser && (
                    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '15px' }}>
                      Zostaniesz poproszony o ponowne zalogowanie przez Google, aby potwierdzić usunięcie konta.
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button 
                      className="btn"
                      onClick={() => {
                        setShowDeleteConfirm(false)
                        setDeletePassword('')
                      }}
                      style={{ 
                        backgroundColor: '#f3f4f6', 
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Anuluj
                    </button>
                    <button 
                      className="btn"
                      onClick={deleteAccount}
                      disabled={deleting || (!isGoogleUser && !deletePassword.trim())}
                      style={{ 
                        backgroundColor: '#dc2626', 
                        color: 'white', 
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        cursor: deleting || (!isGoogleUser && !deletePassword.trim()) ? 'not-allowed' : 'pointer',
                        opacity: deleting || (!isGoogleUser && !deletePassword.trim()) ? 0.6 : 1
                      }}
                    >
                      {deleting ? 'Usuwanie...' : 'Usuń konto na zawsze'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const ClientPanel = ({ isOpen, onClose, isLoggedIn = false, currentUser, onLogout, onProfileUpdated, onLoadConfiguration }: ClientPanelProps) => {
  const [currentView, setCurrentView] = useState<ClientView>('dashboard')
  const [materials, setMaterials] = useState<SharedMaterial[]>([])
  const [savedConfigs, setSavedConfigs] = useState<SavedConfiguration[]>([])
  const [loadingConfigs, setLoadingConfigs] = useState(false)

  // Pagination state
  const [materialsLimit, setMaterialsLimit] = useState(50)
  const [hasMoreMaterials, setHasMoreMaterials] = useState(true)
  const [loadingMoreMaterials, setLoadingMoreMaterials] = useState(false)
  
  // Search state for configurations
  const [configSearchTerm, setConfigSearchTerm] = useState('')
  const [configSearchResults, setConfigSearchResults] = useState<any[]>([])
  const [isSearchingConfigs, setIsSearchingConfigs] = useState(false)
  const [isInConfigSearchMode, setIsInConfigSearchMode] = useState(false)
  const [configSearchResultsSource, setConfigSearchResultsSource] = useState<'cache' | 'database' | null>(null)

  // Reset view to dashboard when panel opens
  useEffect(() => {
    if (isOpen) {
      setCurrentView('dashboard')
    }
  }, [isOpen])

  const refreshMaterials = async () => {
    try {
      const list = await MaterialsService.listSharedMaterials(materialsLimit)
      setMaterials(list)
      setHasMoreMaterials(list.length === materialsLimit)
    } catch (e) {
      console.error(e)
    }
  }

  const loadMoreMaterials = async () => {
    if (loadingMoreMaterials || !hasMoreMaterials) return
    
    try {
      setLoadingMoreMaterials(true)
      const newLimit = materialsLimit + 50
      const list = await MaterialsService.listSharedMaterials(newLimit)
      
      setMaterials(list)
      setMaterialsLimit(newLimit)
      setHasMoreMaterials(list.length === newLimit)
    } catch (e) {
      console.error('Błąd ładowania więcej materiałów:', e)
    } finally {
      setLoadingMoreMaterials(false)
    }
  }

  const searchConfigurations = async (term: string) => {
    if (!currentUser?.id) return
    
    if (!term.trim()) {
      setIsInConfigSearchMode(false)
      setConfigSearchResults([])
      setConfigSearchResultsSource(null)
      return
    }
    
    try {
      setIsSearchingConfigs(true)
      setIsInConfigSearchMode(true)
      const searchLower = term.toLowerCase()
      
      // KROK 1: Przeszukaj najpierw cache (załadowane konfiguracje)
      console.log('🔍 Przeszukiwanie cache (' + savedConfigs.length + ' konfiguracji)...')
      const cacheResults = savedConfigs.filter(config => {
        const searchFields = [
          config.name || '',
          config.modelName || '',
          config.clientNotes || '',
          config.productCode || ''
        ].map(field => field.toLowerCase())
        
        return searchFields.some(field => field.includes(searchLower))
      })
      
      console.log('✅ Znaleziono w cache:', cacheResults.length)
      
      // Jeśli znaleziono wyniki w cache, użyj ich
      if (cacheResults.length > 0) {
        console.log('🏆 Używam wyników z cache (bez zapytania do bazy)')
        // Sortuj tak samo jak w searchUserConfigurations
        const sortedResults = cacheResults.sort((a, b) => {
          const aNameExact = (a.name || '').toLowerCase() === searchLower
          const bNameExact = (b.name || '').toLowerCase() === searchLower
          
          if (aNameExact && !bNameExact) return -1
          if (!aNameExact && bNameExact) return 1
          
          const aDate = a.updatedAt ? new Date(a.updatedAt as any).getTime() : 0
          const bDate = b.updatedAt ? new Date(b.updatedAt as any).getTime() : 0
          return bDate - aDate
        })
        
        setConfigSearchResults(sortedResults)
        setConfigSearchResultsSource('cache')
        setIsSearchingConfigs(false)
        return
      }
      
      // KROK 2: Jeśli cache nie zawiera wyników, przeszukaj pełną bazę
      console.log('🔍 Cache pusty, przeszukiwanie pełnej bazy...')
      const dbResults = await SavedConfigurationsService.searchUserConfigurations(currentUser.id, term)
      console.log('✅ Znaleziono w bazie:', dbResults.length)
      setConfigSearchResults(dbResults)
      setConfigSearchResultsSource('database')
    } catch (e) {
      console.error('❌ Błąd wyszukiwania konfiguracji:', e)
      setConfigSearchResults([])
    } finally {
      setIsSearchingConfigs(false)
    }
  }

  // Debounced search dla konfiguracji
  useEffect(() => {
    if (!currentUser?.id) return
    
    const timeoutId = setTimeout(() => {
      searchConfigurations(configSearchTerm)
    }, 500)
    
    return () => clearTimeout(timeoutId)
  }, [configSearchTerm]) // Removed currentUser?.id to prevent search on user change

  const refreshSavedConfigs = async () => {
    if (!currentUser?.id) return
    
    setLoadingConfigs(true)
    try {
      console.log('Loading configurations for user:', currentUser.id)
      const configs = await SavedConfigurationsService.getUserConfigurations(currentUser.id)
      console.log('Loaded configurations:', configs)
      setSavedConfigs(configs)
    } catch (e: any) {
      console.error('Error loading saved configurations:', e)
      console.error('Error details:', {
        code: e?.code,
        message: e?.message,
        userId: currentUser?.id
      })
      alert(`Błąd podczas ładowania zapisanych konfiguracji: ${e?.message || e}`)
    } finally {
      setLoadingConfigs(false)
    }
  }

  useEffect(() => {
    if (!isOpen || !currentUser?.id) return // Nie ładuj danych jeśli panel jest zamknięty lub brak usera
    
    // Ładuj dane tylko dla aktualnie wybranego widoku
    if (currentView === 'materials') {
      console.log('🎨 Ładowanie materiałów...')
      refreshMaterials()
    } else if (currentView === 'configs') {
      console.log('📋 Ładowanie konfiguracji...')
      refreshSavedConfigs()
    }
    // Profile ładuje dane wewnątrz ClientProfileView
    // Dashboard i settings nie wymagają automatycznego ładowania danych
  }, [currentView, isOpen]) // Removed currentUser?.id to prevent reload on user change

  const renderDashboard = () => (
    <div className="admin-dashboard">
      <h3>Panel Klienta</h3>
      <div className="admin-tiles">
        <div className="admin-tile" onClick={() => setCurrentView('profile')}>
          <div className="tile-icon">👤</div>
          <div className="tile-title">Moje dane</div>
          <div className="tile-description">Zmień e‑mail lub hasło</div>
        </div>
        <div className="admin-tile" onClick={() => setCurrentView('settings')}>
          <div className="tile-icon">⚙️</div>
          <div className="tile-title">Ustawienia</div>
          <div className="tile-description">Profil, e-mail, preferencje</div>
        </div>
        <div className="admin-tile" onClick={() => setCurrentView('materials')}>
          <div className="tile-icon">📁</div>
          <div className="tile-title">Materiały Wspólne</div>
          <div className="tile-description">Pliki od administratora</div>
        </div>
        <div className="admin-tile" onClick={() => setCurrentView('configs')}>
          <div className="tile-icon">🗂️</div>
          <div className="tile-title">Moje zapisane konfiguracje</div>
          <div className="tile-description">Twoje konfiguracje w chmurze</div>
        </div>
        <div className="admin-tile" onClick={() => setCurrentView('orders')}>
          <div className="tile-icon">🧾</div>
          <div className="tile-title">Moje zamówienia</div>
          <div className="tile-description">Statusy i szczegóły zamówień</div>
        </div>
      </div>
    </div>
  )

  const renderPlaceholder = (title: string, description: string) => (
    <div className="admin-placeholder-view">
      <div className="admin-view-header">
        <button className="btn-back" onClick={() => setCurrentView('dashboard')}>
          ← Powrót do panelu głównego
        </button>
        <h3>{title}</h3>
      </div>
      <div className="placeholder-content">
        <p>{description}</p>
        <p><em>Widok testowy. Integracje z Firebase dodamy w kolejnym kroku.</em></p>
      </div>
    </div>
  )

  const renderMaterials = () => (
    <div className="admin-settings">
      <div className="admin-view-header">
        <button className="btn-back" onClick={() => setCurrentView('dashboard')}>
          ← Powrót do panelu głównego
        </button>
        <h3>Materiały Wspólne</h3>
      </div>
      <div className="admin-form" style={{ maxWidth: 900 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Lista materiałów udostępnionych przez administratora</div>
        {materials.length === 0 ? (
          <div style={{ color: '#6b7280' }}>Brak materiałów.</div>
        ) : (
          <div className="materials-list" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
            {materials.map(m => (
              <div key={m.id} className="material-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f9fafb', padding: '10px 12px', borderRadius: 8 }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <div style={{ fontWeight: 600 }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{m.type} • {(m.size / 1024).toFixed(1)} KB</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <a className="btn btn-secondary" href={m.url} target="_blank" rel="noreferrer">Pobierz</a>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {hasMoreMaterials && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <button
              className="btn btn-secondary"
              onClick={loadMoreMaterials}
              disabled={loadingMoreMaterials}
            >
              {loadingMoreMaterials ? 'Załadowanie...' : 'Załaduj więcej materiałów'}
            </button>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Wyświetlanych: {materials.length} materiałów
            </div>
          </div>
        )}
      </div>
    </div>
  )

  const renderSavedConfigs = () => {
    const handleDeleteConfig = async (configId: string) => {
      if (!confirm('Czy na pewno chcesz usunąć tę konfigurację?')) return
      
      try {
        await SavedConfigurationsService.deleteConfiguration(configId)
        await refreshSavedConfigs() // Refresh the list
        alert('Konfiguracja została usunięta')
      } catch (error) {
        console.error('Error deleting configuration:', error)
        alert('Błąd podczas usuwania konfiguracji')
      }
    }

    const handleDuplicateConfig = async (configId: string, currentName: string) => {
      const newName = prompt('Podaj nazwę dla kopii konfiguracji:', `Kopia ${currentName}`)
      if (!newName?.trim()) return
      
      try {
        await SavedConfigurationsService.duplicateConfiguration(configId, newName.trim())
        await refreshSavedConfigs() // Refresh the list
        alert('Konfiguracja została skopiowana')
      } catch (error) {
        console.error('Error duplicating configuration:', error)
        alert('Błąd podczas kopiowania konfiguracji')
      }
    }

    const handleCopyProductCode = async (productCode: string) => {
      try {
        await navigator.clipboard.writeText(productCode)
        alert(`Kod produktu "${productCode}" został skopiowany do schowka`)
      } catch (error) {
        console.error('Error copying to clipboard:', error)
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = productCode
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          alert(`Kod produktu "${productCode}" został skopiowany do schowka`)
        } catch (fallbackError) {
          alert(`Nie można skopiować. Kod produktu: ${productCode}`)
        }
        document.body.removeChild(textArea)
      }
    }

    const handleRenameConfig = async (configId: string, currentName: string) => {
      const newName = prompt('Podaj nową nazwę dla konfiguracji:', currentName)
      if (!newName?.trim() || newName.trim() === currentName) return
      
      try {
        await SavedConfigurationsService.updateConfigurationName(configId, newName.trim())
        await refreshSavedConfigs() // Refresh the list
        alert('Nazwa konfiguracji została zmieniona')
      } catch (error) {
        console.error('Error renaming configuration:', error)
        alert('Błąd podczas zmiany nazwy konfiguracji')
      }
    }

    const handleLoadConfiguration = (config: SavedConfiguration) => {
      if (!onLoadConfiguration) {
        alert('Funkcja ładowania konfiguracji nie jest dostępna')
        return
      }
      
      if (confirm(`Czy na pewno chcesz załadować konfigurację "${config.name}"?\n\nSpowoduje to zastąpienie aktualnej konfiguracji.`)) {
        try {
          // Załaduj konfigurację używając kodu produktu
          onLoadConfiguration(config.productCode)
          
          // Zamknij panel klienta po załadowaniu
          onClose()
          
          alert(`Konfiguracja "${config.name}" została załadowana pomyślnie!`)
        } catch (error) {
          console.error('Error loading configuration:', error)
          alert('Błąd podczas ładowania konfiguracji')
        }
      }
    }

    return (
      <div className="admin-settings">
        <div className="admin-view-header">
          <button className="btn-back" onClick={() => setCurrentView('dashboard')}>
            ← Powrót do panelu głównego
          </button>
          <h3>Moje zapisane konfiguracje</h3>
        </div>
        <div className="admin-form" style={{ maxWidth: 900 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Lista Twoich zapisanych konfiguracji</div>
          
          {/* Pole wyszukiwania */}
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Szukaj konfiguracji po nazwie, modelu lub uwagach..."
              value={configSearchTerm}
              onChange={(e) => setConfigSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            {isInConfigSearchMode && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                {isSearchingConfigs 
                  ? '🔍 Wyszukiwanie...' 
                  : (
                    <>
                      Wyniki wyszukiwania dla: "{configSearchTerm}"
                      {configSearchResultsSource && (
                        <span style={{ marginLeft: 8 }}>
                          {configSearchResultsSource === 'cache' 
                            ? '(💾 z załadowanych)' 
                            : '(📶 z bazy danych)'
                          }
                        </span>
                      )}
                    </>
                  )
                }
              </div>
            )}
          </div>
          
          {(() => {
            if (loadingConfigs) {
              return (
                <div style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>Ładowanie...</div>
              )
            }
            
            if (isSearchingConfigs) {
              return (
                <div style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                  🔍 Wyszukiwanie konfiguracji...
                </div>
              )
            }
            
            // Użyj wyników wyszukiwania lub normalnej listy
            const displayConfigs = isInConfigSearchMode ? configSearchResults : savedConfigs
            
            if (displayConfigs.length === 0) {
              if (isInConfigSearchMode && configSearchTerm.trim()) {
                return (
                  <div style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                    Nie znaleziono konfiguracji pasujących do wyszukiwania "{configSearchTerm}".
                    <br />
                    <small>Przeszukano wszystkie Twoje zapisane konfiguracje.</small>
                  </div>
                )
              } else {
                return (
                  <div style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                    Nie masz jeszcze żadnych zapisanych konfiguracji.
                    <br />
                    <small>Utwórz konfigurację w głównym panelu i zapisz ją używając przycisku "Zapisz" w podsumowaniu.</small>
                  </div>
                )
              }
            }
            
            return (
              <div className="configs-grid">
                {displayConfigs.map(config => (
                <div key={config.id} className="config-card">
                  {/* Header Section */}
                  <div className="config-header" style={{ marginBottom: 16 }}>
                    <h3 style={{ color: '#8B4513', marginBottom: 8 }}>{config.name}</h3>
                    <div style={{ 
                      fontSize: 14, 
                      color: '#6b7280', 
                      marginBottom: 6,
                      fontWeight: 500,
                      lineHeight: 1.4
                    }}>
                      <span style={{ color: '#374151' }}>Model:</span> {config.modelName}
                      <br />
                      <span style={{ color: '#374151' }}>Kod:</span> <span style={{ 
                        fontSize: 13, 
                        color: '#9ca3af',
                        fontFamily: 'monospace',
                        background: '#f3f4f6',
                        padding: '2px 6px',
                        borderRadius: 3,
                        marginLeft: 4
                      }}>{config.productCode}</span>
                      <br />
                      <span style={{ color: '#374151' }}>Uwagi klienta:</span> <span style={{ 
                        fontSize: 13, 
                        color: config.clientNotes?.trim() ? '#555' : '#9ca3af',
                        fontStyle: config.clientNotes?.trim() ? 'normal' : 'italic',
                        marginLeft: 4
                      }}>
                        {config.clientNotes?.trim() || 'brak'}
                      </span>
                    </div>
                  </div>

                  {/* Details Section */}
                  <div className="config-details">
                    <div style={{ marginBottom: 4 }}>
                      <strong>Zapisano:</strong> {config.createdAt ? 
                        (() => {
                          try {
                            // Handle Firebase Timestamp
                            if (config.createdAt && typeof config.createdAt === 'object' && 'toDate' in config.createdAt) {
                              const date = (config.createdAt as any).toDate()
                              return date.toLocaleDateString('pl-PL') + ' ' + date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
                            }
                            // Handle regular Date
                            const date = new Date(config.createdAt as any)
                            if (isNaN(date.getTime())) return 'Nieznana data'
                            return date.toLocaleDateString('pl-PL') + ' ' + date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
                          } catch {
                            return 'Nieznana data'
                          }
                        })() : 'Nieznana data'}
                    </div>
                    {config.pricing && (
                      <div style={{ marginBottom: 4 }}>
                        <strong>Cena netto:</strong> <span style={{ color: '#22C55E', fontWeight: 600 }}>{config.pricing.totalPrice.toLocaleString('pl-PL')} zł</span>
                        <br />
                        <span style={{ fontSize: '0.85em', color: '#666' }}>
                          z VAT 8%: <strong>{(config.pricing.totalPrice * 1.08).toLocaleString('pl-PL', { maximumFractionDigits: 2 })} zł</strong>
                        </span>
                        <br />
                        <span style={{ fontSize: '0.85em', color: '#666' }}>
                          z VAT 23%: <strong>{(config.pricing.totalPrice * 1.23).toLocaleString('pl-PL', { maximumFractionDigits: 2 })} zł</strong>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions Section */}
                  <div className="config-actions">
                    <div className="config-action-wrapper">
                      <button 
                        className="btn btn-compact"
                        style={{ backgroundColor: '#D1B48D', color: 'white', border: 'none' }}
                        onClick={() => handleRenameConfig(config.id!, config.name)}
                        title="Zmień nazwę"
                      >
                        📝
                      </button>
                      <span className="btn-label">Zmień nazwę</span>
                    </div>
                    <div className="config-action-wrapper">
                      <button 
                        className="btn btn-compact"
                        style={{ backgroundColor: '#D1B48D', color: 'white', border: 'none' }}
                        onClick={() => handleCopyProductCode(config.productCode)}
                        title="Kopiuj kod produktu"
                      >
                        📋
                      </button>
                      <span className="btn-label">Kopiuj kod</span>
                    </div>
                    <div className="config-action-wrapper">
                      <button 
                        className="btn btn-compact"
                        style={{ backgroundColor: '#D1B48D', color: 'white', border: 'none' }}
                        onClick={() => handleDuplicateConfig(config.id!, config.name)}
                        title="Duplikuj konfigurację"
                      >
                        📑
                      </button>
                      <span className="btn-label">Duplikuj</span>
                    </div>
                    <div className="config-action-wrapper">
                      <button 
                        className="btn btn-compact"
                        style={{ backgroundColor: '#D1B48D', color: 'white', border: 'none' }}
                        onClick={() => handleLoadConfiguration(config)}
                        title="Załaduj konfigurację"
                      >
                        📂
                      </button>
                      <span className="btn-label">Załaduj</span>
                    </div>
                    <div className="config-action-wrapper">
                      <button 
                        className="btn btn-compact"
                        style={{ backgroundColor: '#D1B48D', color: 'white', border: 'none' }}
                        onClick={() => handleDeleteConfig(config.id!)}
                        title="Usuń"
                      >
                        🗑️
                      </button>
                      <span className="btn-label">Usuń</span>
                    </div>
                  </div>
                </div>
              ))}
              </div>
            )
          })()}
          
          {/* Informacja o liczbie wyświetlanych konfiguracji */}
          {!loadingConfigs && !isSearchingConfigs && (
            <div style={{ marginTop: 16, textAlign: 'center', fontSize: 12, color: '#6b7280' }}>
              {isInConfigSearchMode 
                ? (
                  <>
                    Wyświetlanych: {configSearchResults.length} z wyników wyszukiwania
                    {configSearchResultsSource === 'cache' && (
                      <span style={{ color: '#10b981', marginLeft: 8 }}>⚡ szybkie</span>
                    )}
                  </>
                )
                : `Wyświetlanych: ${savedConfigs.length} konfiguracji`
              }
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return renderDashboard()
      case 'settings':
        return renderPlaceholder('Ustawienia', 'Zmień dane profilu, e‑mail do powiadomień, preferencje RODO. ')
      case 'configs':
        return renderSavedConfigs()
      case 'orders':
        return renderPlaceholder('Moje zamówienia', 'Podgląd statusu i szczegółów zamówień z historią. ')
      case 'materials':
        return renderMaterials()
      case 'profile':
        return <ClientProfileView 
          onBack={() => setCurrentView('dashboard')} 
          currentUser={currentUser} 
          onProfileSaved={u => onProfileUpdated?.(u)}
          onAccountDeleted={() => {
            // Po usunięciu konta wyloguj i zamknij panel
            onLogout?.()
            onClose()
          }}
        />
      default:
        return renderDashboard()
    }
  }

  return (
    <div className={`admin-panel ${isOpen ? 'open' : ''}`}>
      <div className="admin-panel-header">
        <h2>Panel Klienta</h2>
        {isLoggedIn && (
          <button className="admin-logout" onClick={onLogout} title="Wyloguj" style={{ marginRight: 8 }}>
            Wyloguj
          </button>
        )}
        <button className="admin-close" onClick={onClose}>✕</button>
      </div>

      <div className="admin-panel-content">
        {renderCurrentView()}
      </div>
    </div>
  )
}

export default ClientPanel